import { z } from 'zod';
import { EXTRACT_MODES, FIELD_TRANSFORMS, SELECTOR_PACK_LIMITS } from './constants';
import { locatorCandidateSchema } from './locator';

/**
 * Declarative field extraction.
 *
 * A field rule says: find this element, take this part of it, then run these
 * named transforms. Transforms come from a closed list implemented in the agent —
 * the pack chooses among them, it never supplies the implementation.
 *
 * Parsing is tolerant by design. Facebook renders result cards inconsistently and
 * a post missing its share count is still a useful post, so a field that fails to
 * resolve yields `undefined` rather than failing the whole card. Only fields
 * marked `required` (in practice just the post identity) can invalidate a card.
 */
export const fieldRuleSchema = z
  .object({
    candidates: z
      .array(locatorCandidateSchema)
      .min(1)
      .max(SELECTOR_PACK_LIMITS.maxCandidatesPerRule),
    extract: z.enum(EXTRACT_MODES).default('text'),
    /** Attribute name — required when `extract` is `attribute`. */
    attribute: z.string().min(1).max(64).optional(),
    transforms: z
      .array(z.enum(FIELD_TRANSFORMS))
      .max(SELECTOR_PACK_LIMITS.maxTransformsPerField)
      .default([]),
    /** Collect every match instead of only the first (e.g. media URLs). */
    multiple: z.boolean().default(false),
    required: z.boolean().default(false),
    description: z.string().max(300).optional(),
  })
  .strict()
  .refine((rule) => rule.extract !== 'attribute' || Boolean(rule.attribute), {
    message: 'extract: "attribute" requires an `attribute` name',
    path: ['attribute'],
  })
  /**
   * `count`/`exists` are answered from the MATCH SET before the winning
   * candidate is even known, so a candidate-level override of either could
   * never take effect — the schema refuses the combination instead of letting
   * a pack author believe it did something.
   */
  .refine(
    (rule) =>
      rule.candidates.every(
        (candidate) => candidate.extract !== 'count' && candidate.extract !== 'exists',
      ),
    {
      message: 'candidate-level extract cannot be "count" or "exists" — they are rule-level answers',
      path: ['candidates'],
    },
  );

export type FieldRule = z.infer<typeof fieldRuleSchema>;
